import {
    Formik,
    Form,
    Field,
    ErrorMessage
} from 'formik';

import { observer } from 'mobx-react-lite';
import { Box, Button, Checkbox, Divider, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography, useMediaQuery }
    from '@mui/material';
import { ExistingPartner, NaviLines, SubPartnersKobHoved, Ta1AdminKobhoved, Ta1AdminKobhovedPartnerFormValue }
    from '../../../app/models/partners';
import { collectionFormStyles } from '../../basicUser/basicUserForms/collectionFormStyles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { adminNewPartnerSchema } from '../../../app/common/formValidations/validationSchemes';
import { useStore } from '../../../app/stores/store';
import ValidationErrors from '../../errors/ValidationErrors';
import { adminUserStyles } from '../adminUsers/adminUserStyles';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';

interface Props {
    selectedKobhoveds: Ta1AdminKobhoved[] | SubPartnersKobHoved[];
    backBtnHandler: () => void;
    backBtnHandlerToSearch?: () => void;
    select?: boolean;
    selectPartner?: ExistingPartner;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const NewPartnerForm = ({ selectedKobhoveds, backBtnHandler, backBtnHandlerToSearch, selectPartner }: Props) => {

    const { userStore, adminStore } = useStore();
    const { user } = userStore;
    const { createPartnerLoading } = adminStore;

    const matches = useMediaQuery('(min-width:600px)');
    
    const initialValues: Ta1AdminKobhovedPartnerFormValue = {
        name: selectPartner !== undefined ? selectPartner.userName : '',
        email: selectPartner !== undefined ? selectPartner.email : '',
        ownerRole: user?.currentUserRole,
        roles: [],
        userOption: {
            see_Download_KoReport: false,
            see_Download_ManagementReport: false,
            see_Download_Sbi: false,
            see_Download_AcknowledgementReport: false,
            create_Collection: false,
            approve_Sbi: false,
            see_All_Collection_Specific_Debitor: false,
            see_All_Collection_Specific_Creditor: false,
            seseller_Approve_Crf_Customer_Request: false,
        },
        purchaseNos: [],
        error: null
    }

    return (
        <React.Fragment>
            {selectedKobhoveds &&
                <Box component="div" sx={collectionFormStyles.newPartnerFormContainer}>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={adminNewPartnerSchema}
                        onSubmit={(values: Ta1AdminKobhovedPartnerFormValue, { setErrors }) => {

                            const purchaseNos: NaviLines[] = [];

                            selectedKobhoveds.forEach(element => {
                                element.naviLines.forEach(navi => {
                                    purchaseNos.push(navi)
                                })

                            });

                            const payload: Ta1AdminKobhovedPartnerFormValue = {
                                email: values.email,
                                name: values.name,
                                ownerRole: values.ownerRole,
                                roles: values.roles,
                                userOption: values.userOption,
                                purchaseNos: purchaseNos,
                                error: null
                            }

                            if (selectPartner !== undefined) {
                                adminStore.createExistingPartner(payload)
                                    .then(() => backBtnHandlerToSearch && backBtnHandlerToSearch())
                                    .catch(error => setErrors({ error }))
                            } else {
                                adminStore.createPartner(payload)
                                    .then(() => backBtnHandler())
                                    .catch(error => setErrors({ error }))
                            }
                        }}
                    >
                        {({ values,
                            handleChange,
                            handleBlur,
                            errors
                        }) => {
                            return (
                                <Form autoComplete="off">
                                    <Box sx={collectionFormStyles.partnerFormControls}>
                                        {selectPartner !== undefined ?
                                            <Button sx={{ width: 200 }} size='medium' variant='outlined' onClick={() => backBtnHandler()}>Back to search</Button>
                                            :
                                            <ArrowBackIcon sx={{ color: '#000', cursor: 'pointer' }} onClick={() => backBtnHandler()} />
                                        }
                                        <Divider sx={{ m: '10px 10px' }} />
                                        <Stack direction='column' spacing={2} m={2} alignItems='center'>
                                            <FormControl sx={{ width: 300 }}>
                                                <Field
                                                    type="text"
                                                    label="Name"
                                                    component={TextField}
                                                    id="name"
                                                    name="name"
                                                    placeholder="name"
                                                    fullWidth
                                                    margin="normal"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.name}
                                                    variant="outlined" />
                                                <ErrorMessage name="name" component="div" className='errorTextBlack' />
                                            </FormControl>
                                            <FormControl sx={{ width: 300 }}>
                                                <Field
                                                    type="text"
                                                    label="Email"
                                                    component={TextField}
                                                    id="email"
                                                    name="email"
                                                    placeholder="Email"
                                                    fullWidth
                                                    margin="normal"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.email}
                                                    variant="outlined"
                                                />
                                                <ErrorMessage name="email" component="div" className='errorTextBlack' />
                                            </FormControl>
                                            <FormControl sx={{ width: 300 }}>
                                                <InputLabel id="roles">Roles</InputLabel>
                                                <Select
                                                    labelId="roles"
                                                    id="roles"
                                                    name="roles"
                                                    multiple
                                                    value={values.roles}
                                                    label="Roles"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    sx={{ minWidth: 180 }}
                                                    MenuProps={MenuProps}
                                                >
                                                    {user?.partnerRolePriorities.map((role) => (
                                                        <MenuItem value={role.partnerRole} key={role.id}>{role.partnerRole}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <ErrorMessage
                                                name='error' render={() =>
                                                    <ValidationErrors errors={errors.error} />
                                                }
                                            />
                                        </Stack>
                                    </Box>

                                    <Stack
                                        direction={matches ? "row" : "column"}
                                        justifyContent="center"
                                    >
                                        <Stack
                                            direction="column"
                                            alignItems="center"
                                            width={300}
                                            spacing={2}
                                            pt={2}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                width={300}
                                                spacing={2}
                                                pt={2}

                                            >
                                                <Checkbox
                                                    checked={values.userOption.see_Download_KoReport}
                                                    name='userOption.see_Download_KoReport'
                                                    value={values.userOption.see_Download_KoReport}
                                                    onChange={handleChange}
                                                    sx={adminUserStyles.adminCheckBoxStyle}
                                                />
                                                <Typography>See download Ko report</Typography>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                width={300}
                                                spacing={2}
                                                pt={2}

                                            >
                                                <Checkbox
                                                    checked={values.userOption.see_Download_ManagementReport}
                                                    value={values.userOption.see_Download_ManagementReport}
                                                    name='userOption.see_Download_ManagementReport'
                                                    onChange={handleChange}
                                                    sx={adminUserStyles.adminCheckBoxStyle}
                                                />
                                                <Typography>See download management report</Typography>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                width={300}
                                                spacing={2}
                                                pt={2}

                                            >
                                                <Checkbox
                                                    checked={values.userOption.see_Download_Sbi}
                                                    value={values.userOption.see_Download_Sbi}
                                                    name='userOption.see_Download_Sbi'
                                                    onChange={handleChange}
                                                    sx={adminUserStyles.adminCheckBoxStyle}
                                                />
                                                <Typography>See download SBI</Typography>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                width={300}
                                                spacing={2}
                                                pt={2}

                                            >
                                                <Checkbox
                                                    checked={values.userOption.see_Download_AcknowledgementReport}
                                                    value={values.userOption.see_Download_AcknowledgementReport}
                                                    name='userOption.see_Download_AcknowledgementReport'
                                                    onChange={handleChange}
                                                    sx={adminUserStyles.adminCheckBoxStyle}
                                                />
                                                <Typography>See download AcknowledgementReport</Typography>
                                            </Stack>
                                        </Stack>
                                        <Stack
                                            direction="column"
                                            alignItems="center"
                                            width={300}
                                            spacing={2}
                                            pt={2}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                width={300}
                                                spacing={2}
                                                pt={2}

                                            >
                                                <Checkbox
                                                    checked={values.userOption.create_Collection}
                                                    value={values.userOption.create_Collection}
                                                    name='userOption.create_Collection'
                                                    onChange={handleChange}
                                                    sx={adminUserStyles.adminCheckBoxStyle}
                                                />
                                                <Typography>Create Collection</Typography>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                width={300}
                                                spacing={2}
                                                pt={2}

                                            >
                                                <Checkbox
                                                    checked={values.userOption.approve_Sbi}
                                                    value={values.userOption.approve_Sbi}
                                                    name='userOption.approve_Sbi'
                                                    onChange={handleChange}
                                                    sx={adminUserStyles.adminCheckBoxStyle}
                                                />
                                                <Typography>Approve SBI</Typography>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                width={300}
                                                spacing={2}
                                                pt={2}
                                            >
                                                <Checkbox
                                                    checked={values.userOption.see_All_Collection_Specific_Debitor}
                                                    value={values.userOption.see_All_Collection_Specific_Debitor}
                                                    name='userOption.see_All_Collection_Specific_Debitor'
                                                    onChange={handleChange}
                                                    sx={adminUserStyles.adminCheckBoxStyle}
                                                />
                                                <Typography>See all collection specific debitor</Typography>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                width={300}
                                                spacing={2}
                                                pt={2}

                                            >
                                                <Checkbox
                                                    checked={values.userOption.see_All_Collection_Specific_Creditor}
                                                    value={values.userOption.see_All_Collection_Specific_Creditor}
                                                    name='userOption.see_All_Collection_Specific_Creditor'
                                                    onChange={handleChange}
                                                    sx={adminUserStyles.adminCheckBoxStyle}
                                                />
                                                <Typography>See all collection specific creditors</Typography>
                                            </Stack>

                                        </Stack>
                                    </Stack>
                                    <Stack
                                        direction="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        spacing={2}
                                        pt={5}
                                    >
                                        {createPartnerLoading ? <CircularProgress size='1.2rem' /> :
                                            <Button size="medium" variant="contained" name='create' id='create' type='submit' >Create</Button>
                                        }
                                    </Stack>
                                </Form>

                            );
                        }}

                    </Formik>
                </Box>
            }
        </React.Fragment>
    );
}
export default observer(NewPartnerForm);